import ajax, { axiosInstance, setDefaults } from '@fountain/ui-kit/helpers/ajax';
import notify from '@fountain/ui-kit/components/notifications';
import { camelToUnderscoreNested, underscoreToCamelNested } from './transformations';
import { getCSRF } from './shared';

const globalOptions = {
  signInUrl: '/',
};

export const isJsonResponse = response => response && typeof response.data === 'object';

export const isClientError = error => error.response && error.response.status >= 400 && error.response.status < 500;

export const isValidationReject = error => error.response && error.response.status === 422;

export const handleError = (error) => {
  if (isJsonResponse(error.response) && error.response.data.error) {
    notify(error.response.data.error);
  }
};

export const handleRedirect = (response) => {
  if (isJsonResponse(response) && response.data.redirectTo) {
    window.location = response.data.redirectTo;
  }

  return response;
};

export const setup = opts => Object.assign(globalOptions, opts);

function setHeader(headers, header, value) {
  if (typeof headers !== 'object') {
    return null;
  }

  const preparedHeader = header.replace(/\b(\w)/g, (all, letter) => letter.toUpperCase());

  return Object.assign(headers, { [preparedHeader]: value });
}

function getHeader(headers, header) {
  if (typeof headers !== 'object') {
    return null;
  }

  const originHeader = Object.keys(headers).find(h => h.toLowerCase() === header.toLowerCase());

  if (originHeader) {
    return headers[originHeader];
  }

  return null;
}

axiosInstance.interceptors.request.use((requestConfig) => {
  const csrf = getCSRF();

  if (csrf) {
    setHeader(requestConfig.headers, 'x-csrf-token', csrf.token);
  }

  if (typeof requestConfig.data === 'object' && Object.prototype.toString.call(requestConfig.data).slice(8, -1) === 'Object') {
    setHeader(requestConfig.headers, 'content-type', 'application/json;charset=utf-8');
  }

  const contentType = getHeader(requestConfig.headers, 'content-type');

  if (contentType && contentType.includes('application/json')) {
    Object.assign(requestConfig, { data: camelToUnderscoreNested(requestConfig.data) });
  }

  return requestConfig;
});

axiosInstance.interceptors.response.use((response) => {
  const contentType = getHeader(response.headers, 'content-type');

  if (response.status < 500 && contentType && contentType.includes('application/json')) {
    Object.assign(response, { data: underscoreToCamelNested(response.data) });
  }

  return response;
});

setDefaults({
  defaultRejectHandler: (error, _requestConfig, actionName, options) => {
    const { status, statusText } = error.response;
    let timeout = 3000; // This matches the notification default timeout

    if (!options.ignoreAuthError && status === 401) {
      window.location = globalOptions.signInUrl;
      return;
    }

    if (process.env.NODE_ENV === 'development') {
      timeout = 30000; // Leave the notification visible long enough to help with debugging
      console.error(`AJAX_ERROR ${actionName}:${statusText}`, error);
    }

    // Looking for how Toast notifications or messages (aka Flash notices) get shown in V2 components?
    // Well, it typicaly happens via the call to `notify()` below.
    // You may also be interested in these items below:
    //    front/v1/helpers/obiq-ajax.js - the `Messenger().post()` function call
    //    front/v2/helpers/form.js - the `alert()` function call
    if (isClientError(error) && isJsonResponse(error.response) && error.response.data.error) {
      notify(error.response.data.error, 'error', { timeout });
    } else if (!options.hideDefaultErrorNotification) {
      notify('An error occurred', 'error');
    }
  },
});

export default ajax;
